/**
 * The invite sheet's frame: title, the role-correct sharing header, and
 * three source tabs, `People`, `Chats`, `Pools`. `People`'s live wiring
 * to `/invite/user-search` is WI-9, gated on ISSUE-189. `Chats` and `Pools`
 * are ISSUE-197: `Pools` bulk-invites out of another pool the caller runs,
 * `Chats` bulk-invites out of a chat the caller is in (ISSUE-1097).
 */
import {
  useInviteSend,
  useInviteUserSearch,
  usePoolGet,
} from "@sportsmet/pool-sdk/private";
import { Dialog, Tabs, type typeTabItem } from "@sportsmet/ui";
import { onlineManager } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";

import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import { InviteChatsTab } from "./InviteChatsTab";
import { InvitePeopleTab } from "./InvitePeopleTab";
import { InvitePoolsTab } from "./InvitePoolsTab";
import {
  resolveRowStateAfterSend,
  type typeInviteSearchHit,
} from "./inviteResultRows";
import { InviteShareHeader } from "./InviteShareHeader";
import {
  CHATS_TAB_LABEL,
  INVITE_SHEET_TITLE_PREFIX,
  PEOPLE_TAB_LABEL,
  POOLS_TAB_LABEL,
} from "./inviteSheetCopy";
import type { typeInviteTab } from "./inviteSheetRoute";

/**
 * ISSUE-1187: The shared Dialog sizes the invite sheet to its content, so
 * switching tabs moved the tab row. A fixed height keeps both the tab row and
 * the top edge still. 85dvh is the Dialog's own cap and 40rem (640px at 16px
 * root) keeps a desktop sheet from growing past what the lists need. The
 * Dialog's scroll body (`dialog-body`) scrolls whatever does not fit.
 */
const INVITE_SHEET_HEIGHT_CLASS = "h-[min(85dvh,40rem)]";

export type typeInviteSheetProps = {
  open: boolean;
  poolId: string;
  activeTab: typeInviteTab;
  onTabChange: (next: typeInviteTab) => void;
  query: string;
  onQueryChange: (next: string) => void;
  onOpenChange: (open: boolean) => void;
};

export const InviteSheet = ({
  open,
  poolId,
  activeTab,
  onTabChange,
  query,
  onQueryChange,
  onOpenChange,
}: typeInviteSheetProps): ReactNode => {
  const pool = usePoolGet({ poolId });
  const poolName = pool.data?.data?.pool.name ?? "";
  const viewerPoolRole = pool.data?.data?.viewerPoolRole ?? "member";

  // A one-character query would fire a global search on every keystroke.
  // Two is the smallest floor that stops that. It is not a validation error:
  // below two characters the tab simply stays in its `idle` phase.
  const trimmedQuery = query.trim();
  const results = useInviteUserSearch(
    { poolId, query: trimmedQuery },
    { enabled: open && trimmedQuery.length >= 2 },
  );

  useEffect(() => {
    const code = extractErrorCode(results.data);
    if (code !== null)
      showApiError(results.data, undefined, results.dataUpdatedAt);
  }, [poolId, results.dataUpdatedAt, trimmedQuery]);

  const send = useInviteSend();
  const [locallyInvited, setLocallyInvited] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [inPool, setInPool] = useState<ReadonlySet<string>>(new Set());
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  async function handleInvite(targetUserId: string): Promise<void> {
    setBusyUserId(targetUserId);
    let result: Awaited<ReturnType<typeof send.mutateAsync>>;
    try {
      result = await send.mutateAsync({ poolId, targetUserId });
    } catch {
      // Same rule as `InviteBulkConfirm`'s catch, and it has to be the same
      // rule: one sheet must not answer one failure two ways depending on
      // which tab the fan is on. The MutationCache handler in
      // `../../queryClient.ts` owns the online half, so calling
      // unconditionally here raised the modal twice for one throw. It does not
      // own the offline half, so leaving the call out entirely would let the
      // row drop back to `Invite` with nothing said. See the longer note in
      // `InviteBulkConfirm.tsx` for why the `OfflineBanner` behind the scrim
      // does not cover that.
      if (!onlineManager.isOnline()) showOfflineError();
      setBusyUserId(null);
      return;
    }
    if (showApiError(result)) {
      setBusyUserId(null);
      return;
    }
    const outcome = result.data?.outcome;
    if (outcome === undefined) {
      setBusyUserId(null);
      return;
    }
    const next = resolveRowStateAfterSend(outcome);
    if (next === "invited") {
      setLocallyInvited((prev) => new Set(prev).add(targetUserId));
    }
    if (next === "inPool") {
      setInPool((prev) => new Set(prev).add(targetUserId));
    }
    setBusyUserId(null);
  }

  let peoplePhase: "idle" | "searching" | "ready";
  if (trimmedQuery.length < 2) {
    peoplePhase = "idle";
  } else if (results.isError || extractErrorCode(results.data) !== null) {
    peoplePhase = "idle";
  } else if (results.isLoading) {
    peoplePhase = "searching";
  } else {
    peoplePhase = "ready";
  }

  const hits: readonly typeInviteSearchHit[] = results.data?.data?.items ?? [];
  const hitsWithInPoolApplied = hits.map((hit) =>
    inPool.has(hit.userId) ? { ...hit, relation: "member" as const } : hit,
  );

  const items: typeTabItem[] = [
    {
      value: "people",
      label: PEOPLE_TAB_LABEL,
      content: (
        <InvitePeopleTab
          phase={peoplePhase}
          query={query}
          onQueryChange={onQueryChange}
          hits={hitsWithInPoolApplied}
          locallyInvited={locallyInvited}
          busyUserId={busyUserId}
          onInvite={(userId) => {
            void handleInvite(userId);
          }}
        />
      ),
    },
    {
      value: "chats",
      label: CHATS_TAB_LABEL,
      content: <InviteChatsTab poolId={poolId} />,
    },
    {
      value: "pools",
      label: POOLS_TAB_LABEL,
      content: <InvitePoolsTab poolId={poolId} />,
    },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${INVITE_SHEET_TITLE_PREFIX}${poolName}`}
      autoFocus={true}
      className={INVITE_SHEET_HEIGHT_CLASS}
    >
      <div className="flex flex-col gap-4" data-testid="invite-sheet">
        <InviteShareHeader
          poolId={poolId}
          poolName={poolName}
          viewerPoolRole={viewerPoolRole}
        />
        <Tabs
          label="Invite to pool"
          items={items}
          value={activeTab}
          onValueChange={(next) => {
            onTabChange(next as typeInviteTab);
          }}
        />
      </div>
    </Dialog>
  );
};
